// src/components/Navbar.js

import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Container,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Divider,
  Box,
  IconButton,
  Slide,
  Fade,
  Stack,
  useTheme,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NightsStayIcon from "@material-ui/icons/NightsStay";
import WbSunnyIcon from "@material-ui/icons/WbSunny";
import SidebarToggleIcon from "./SidebarToggleIcon";
import { Link, useRouter } from "./../util/router";
import { useAuth } from "./../util/auth";
import { useDarkMode } from "./../util/theme";
import useMediaQuery from "@material-ui/core/useMediaQuery";

// Custom navbar visibility control that shows when scrolling up
function HideOnScroll(props) {
  const { children, isProPage, isMobile } = props;
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const lastDirection = useRef("none");
  const ticking = useRef(false);

  useEffect(() => {
    // Always show navbar on Pro page in any view
    if (isProPage) {
      setVisible(true);
      return;
    }

    const controlNavbar = () => {
      const currentScrollY = window.scrollY;

      // Determine scroll direction
      if (currentScrollY < lastScrollY.current) {
        // Scrolling UP - always show navbar
        lastDirection.current = "up";
        setVisible(true);
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
        // Scrolling DOWN and not at the top - hide navbar
        lastDirection.current = "down";
        setVisible(false);
      }

      lastScrollY.current = currentScrollY;
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        // Use requestAnimationFrame to limit the number of DOM updates
        window.requestAnimationFrame(controlNavbar);
        ticking.current = true;
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", onScroll);

    // Clean up
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [isProPage, isMobile]);

  return (
    <Slide appear={false} direction="down" in={visible}>
      {children}
    </Slide>
  );
}

function Navbar(props) {
  const { onSidebarToggle } = props;
  const auth = useAuth();
  const darkMode = useDarkMode();
  const theme = useTheme();
  const router = useRouter();
  const [menuState, setMenuState] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // For responsive breakpoints
  const isMobile = useMediaQuery("(max-width:900px)");

  // Check if we're on the Pro page
  const isProPage = router.pathname === "/pro";

  // Use inverted logo if specified and we are in dark mode
  const logo =
    props.logoInverted && darkMode.value ? props.logoInverted : props.logo;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleOpenMenu = (event, id) => {
    setMenuState({ anchor: event.currentTarget, id });
  };

  const handleCloseMenu = () => {
    setMenuState(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navLinks = [
    {
      title: "Features",
      path: "https://www.resumerevival.xyz/features/",
      external: true,
    },
    { title: "Pricing", path: "/pricing" },
    {
      title: "About",
      path: "https://www.resumerevival.xyz/about/",
      external: true,
    },
    { title: "FAQ", path: "/faq" },
  ];

  // Get current colors based on theme
  const textColor = darkMode.value ? "#fff" : "#000";
  const accentColor = darkMode.value ? "#FAD9CF" : theme.palette.primary.main;

  return (
    <>
      <HideOnScroll isProPage={isProPage} isMobile={isMobile}>
        <AppBar
          position="fixed"
          color="default"
          elevation={0}
          sx={{
            py: isProPage
              ? scrolled
                ? 1.5
                : 2 // Taller navbar on Pro page in any view
              : scrolled
              ? 0.5
              : 1, // Normal height on other pages
            transition: "all 0.3s ease",
            boxShadow: scrolled
              ? darkMode.value
                ? "0 4px 20px rgba(0, 0, 0, 0.2)"
                : "0 4px 20px rgba(0, 0, 0, 0.05)"
              : "none",
            color: textColor,
            backgroundColor: darkMode.value
              ? "rgba(30, 30, 30, 0.9)"
              : "rgba(255, 255, 255, 0.95)",
          }}
        >
          {/* Sidebar Toggle Button - only show when user is logged in and on pro page */}
          {auth?.user && isProPage && (
            <Tooltip title="View Past Application Materials">
              <IconButton
                color="inherit"
                edge="start"
                type="button"
                aria-label="View Past Application Materials"
                onClick={onSidebarToggle}
                sx={{
                  position: "absolute",
                  left: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1100,
                }}
              >
                <SidebarToggleIcon />
              </IconButton>
            </Tooltip>
          )}
          <Container maxWidth="lg">
            <Toolbar
              disableGutters
              sx={{
                justifyContent:
                  isProPage && isMobile ? "center" : "space-between",
                position: "relative",
              }}
            >
              {/* Brand Logo - centered on mobile when on pro page */}
              <a
                href="https://www.resumerevival.xyz"
                style={{
                  display: "inline-block",
                  position: isProPage && isMobile ? "static" : "relative",
                  margin: isProPage ? "0 auto" : "0", // Center the logo when on pro page
                }}
              >
                <img
                  src={logo}
                  alt="Resume Revival Logo"
                  style={{
                    height: 32,
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
              </a>

              {/* Desktop Navigation */}
              {!isMobile && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {navLinks.map((link, index) => (
                    <Button
                      key={index}
                      component={link.external ? "a" : Link}
                      href={link.external ? link.path : undefined}
                      to={!link.external ? link.path : undefined}
                      color="inherit"
                      sx={{
                        margin: "0 8px",
                        fontWeight: 500,
                        textTransform: "none",
                        fontSize: "1rem",
                        position: "relative",
                        color: textColor,
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "6px",
                          left: "50%",
                          width: "0%",
                          height: "2px",
                          backgroundColor: accentColor,
                          transition: "all 0.3s ease",
                          transform: "translateX(-50%)",
                        },
                        "&:hover::after": {
                          width: "60%",
                        },
                      }}
                    >
                      {link.title}
                    </Button>
                  ))}

                  <Box sx={{ mx: 1 }}>
                    <IconButton
                      color="inherit"
                      onClick={darkMode.toggle}
                      sx={{ opacity: 0.8 }}
                    >
                      {darkMode.value ? <NightsStayIcon /> : <WbSunnyIcon />}
                    </IconButton>
                  </Box>

                  {/* Account Menu */}
                  {auth?.user ? (
                    <Button
                      color="inherit"
                      aria-label="Account"
                      aria-controls="account-menu"
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, "account-menu")}
                      endIcon={<ExpandMoreIcon />}
                      sx={{
                        ml: 1,
                        borderRadius: "8px",
                        px: 2,
                        py: 0.75,
                        color: textColor,
                        backgroundColor: darkMode.value
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(63, 81, 181, 0.05)",
                      }}
                    >
                      Account
                    </Button>
                  ) : (
                    <Button
                      component={Link}
                      to="/auth/signin"
                      variant="contained"
                      color="primary"
                      sx={{
                        ml: 1,
                        borderRadius: "8px",
                        px: 3,
                        py: 0.75,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      Sign in
                    </Button>
                  )}
                </Stack>
              )}

              {/* Mobile Menu Toggle */}
              {isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: isProPage ? "absolute" : "static",
                    right: isProPage ? 16 : "auto",
                  }}
                >
                  <IconButton
                    color="inherit"
                    onClick={darkMode.toggle}
                    sx={{ opacity: 0.8 }}
                  >
                    {darkMode.value ? <NightsStayIcon /> : <WbSunnyIcon />}
                  </IconButton>
                  <IconButton
                    color="inherit"
                    onClick={toggleMobileMenu}
                    sx={{ ml: 1, color: textColor }}
                  >
                    {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Account Menu Dropdown */}
      <Menu
        id="account-menu"
        open={Boolean(menuState && menuState.id === "account-menu")}
        anchorEl={menuState ? menuState.anchor : null}
        onClick={handleCloseMenu}
        onClose={handleCloseMenu}
        keepMounted
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            borderRadius: "12px",
            minWidth: "180px",
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
            "& .MuiMenuItem-root": {
              px: 2,
              py: 1.5,
              borderRadius: "8px",
              mx: 0.5,
              my: 0.25,
              fontSize: "0.95rem",
            },
          },
        }}
      >
        {auth?.user && [
          <MenuItem
            key="dashboard"
            component={Link}
            to="/dashboard"
            onClick={handleCloseMenu}
          >
            Dashboard
          </MenuItem>,
          <MenuItem
            key="settings"
            component={Link}
            to="/settings/general"
            onClick={handleCloseMenu}
          >
            Settings
          </MenuItem>,
          <Divider key="divider" sx={{ my: 1 }} />,
          <MenuItem
            key="signout"
            onClick={() => {
              auth.signout();
              handleCloseMenu();
            }}
            sx={{
              color: theme.palette.error.main,
            }}
          >
            Sign out
          </MenuItem>,
        ]}
      </Menu>

      {/* Mobile Menu */}
      {isMobile && (
        <Fade in={mobileMenuOpen}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
              padding: "80px 24px 24px",
              backdropFilter: "blur(10px)",
              backgroundColor: darkMode.value
                ? "rgba(30, 30, 30, 0.9)"
                : "rgba(255, 255, 255, 0.95)",
              color: textColor,
            }}
          >
            <IconButton
              color="inherit"
              onClick={toggleMobileMenu}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: textColor,
              }}
            >
              <CloseIcon />
            </IconButton>

            <Stack spacing={2} sx={{ mb: 4 }}>
              {navLinks.map((link, index) => (
                <Button
                  key={index}
                  component={link.external ? "a" : Link}
                  href={link.external ? link.path : undefined}
                  to={!link.external ? link.path : undefined}
                  color="inherit"
                  size="large"
                  onClick={toggleMobileMenu}
                  sx={{
                    justifyContent: "flex-start",
                    fontSize: "1.25rem",
                    py: 1.5,
                    borderRadius: "8px",
                    textTransform: "none",
                    color: textColor,
                  }}
                >
                  {link.title}
                </Button>
              ))}
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {auth?.user ? (
              <Stack spacing={2}>
                <Button
                  component={Link}
                  to="/dashboard"
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={toggleMobileMenu}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Dashboard
                </Button>
                <Button
                  onClick={() => {
                    auth.signout();
                    toggleMobileMenu();
                  }}
                  variant="text"
                  color="inherit"
                  size="large"
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    color: textColor,
                  }}
                >
                  Sign out
                </Button>
              </Stack>
            ) : (
              <Button
                component={Link}
                to="/auth/signin"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                sx={{
                  borderRadius: "8px",
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Sign in
              </Button>
            )}
          </Box>
        </Fade>
      )}
    </>
  );
}

export default Navbar;
